import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { SITE_URL, routeUrl } from '../seo/routes';

/**
 * The head of one page.
 *
 * ── Why every tag here also had to be DELETED from public/index.html ────────
 * react-helmet-async only removes head tags it owns, the ones carrying
 * data-rh. A title, description, canonical or og:* left hardcoded in
 * index.html therefore survives alongside Helmet's, and the page ships two
 * canonicals. Two canonicals is worse than none: the crawler picks one, and it
 * may well pick the wrong one. So index.html keeps the charset, the viewport,
 * the icons and the fonts, and nothing that varies by page.
 *
 * ── The canonical is the whole reason this component exists ─────────────────
 * Every route used to carry <link rel="canonical" href="https://www.dynapage.co.uk/">,
 * so /nizro told Google it was not the real page and the real page was the home
 * page. All 210 Arabic and English Nizro strings were invisible for as long as
 * that line was there.
 */
export function Seo({ page, path, jsonLd }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const canonical = routeUrl(lang, path);
  const title = t(`${page}.metaTitle`);
  const description = t(`${page}.metaDescription`);
  const ogImage = `${SITE_URL}/og/${page}-${lang}.png`;

  return (
    <Helmet prioritizeSeoTags>
      <html lang={lang} dir={lang === 'ar' ? 'rtl' : 'ltr'} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Both languages, plus x-default. Pointing all three at one URL, which
          is what the old index.html did, cancels the whole set out. */}
      <link rel="alternate" hrefLang="en-gb" href={routeUrl('en', path)} />
      <link rel="alternate" hrefLang="ar" href={routeUrl('ar', path)} />
      <link rel="alternate" hrefLang="x-default" href={routeUrl('en', path)} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="DynaCloud Ltd" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={lang === 'ar' ? 'ar_OM' : 'en_GB'} />
      <meta property="og:locale:alternate" content={lang === 'ar' ? 'en_GB' : 'ar_OM'} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
}

/**
 * Service + FAQ + breadcrumbs for a page that sells one thing.
 *
 * Service rather than Product: this is work delivered by people, priced per
 * engagement, and schema.org draws the distinction. No aggregateRating and no
 * review count anywhere, because there is no review corpus and inventing one
 * is the kind of claim that was stripped out of this site once already.
 */
export function serviceJsonLd({ lang, path, name, description, faq }) {
  const url = routeUrl(lang, path);
  const home = routeUrl(lang, '');
  const graph = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${url}#service`,
      name,
      description,
      serviceType: name,
      provider: { '@type': 'Organization', name: 'DynaCloud Ltd', url: SITE_URL },
      areaServed: [
        { '@type': 'Country', name: 'Oman' },
        { '@type': 'Country', name: 'United Arab Emirates' },
        { '@type': 'Country', name: 'Qatar' },
        { '@type': 'Country', name: 'Saudi Arabia' },
      ],
      availableLanguage: ['ar', 'en'],
      url,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: lang === 'ar' ? 'الرئيسية' : 'Home', item: home },
        { '@type': 'ListItem', position: 2, name, item: url },
      ],
    },
  ];
  if (faq && faq.length) {
    graph.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: lang,
      mainEntity: faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }
  return graph;
}
